import { api } from "./client";
import type { PagedQuery, PagedResult } from "@/types/api";
import type { PreleadDetailDto } from "@/api/leadGen/preleads";

// --- Enum string unions (serialized by ASP.NET as enum name by default
// for [FromQuery] but numeric for [FromBody]. We use strings and rely
// on System.Text.Json's enum conversion — confirmed by existing broker API.)

export type LeadSource =
  | "ColdCall" | "InboundInquiry" | "TradeShow" | "Referral" | "WebForm"
  | "SocialMedia" | "IBBA" | "MaSource" | "BizBuySell" | "LinkedIn"
  | "PurchasedList" | "Other" | "ExternalGenerator"
  // Buyer CRM — system-set when a listing agent is escalated into broker
  // recruitment. In the union so such a lead renders; deliberately NOT in the
  // SOURCES picker arrays, exactly like ExternalGenerator, because no human
  // selects it.
  | "ListingAgent";

export type LeadStatus =
  | "New" | "Contacted" | "Interested" | "NotInterested"
  | "Unresponsive" | "HandedOff" | "Disqualified";

export type InterestLevel = "Unknown" | "Cold" | "Warm" | "Hot";

export type CallDisposition =
  | "Connected" | "Voicemail" | "NoAnswer" | "Busy"
  | "CallbackRequested" | "WrongNumber" | "Disconnected";

export type TaskPriority = "Low" | "Normal" | "High" | "Urgent";
export type BrokerTaskStatus = "Pending" | "InProgress" | "Completed" | "Cancelled";
export type BrokerTaskType =
  | "FollowUpCall" | "SendEmail" | "SendDocuments" | "ReviewApplication"
  | "ScheduleMeeting" | "TrainingReminder" | "ComplianceCheck" | "General";

export type DisqualificationReason =
  | "NoRequiredLicense" | "WrongDealType" | "GeographicMismatch"
  | "RegulatoryIssue" | "Unresponsive" | "NotInterested"
  | "CompetitorRelationship" | "DuplicateRecord" | "Other";

export type ImportStatus = "Processing" | "Completed" | "CompletedWithErrors" | "Failed";

// --- DTOs ----------------------------------------------------------------

export interface BrokerLeadSummaryDto {
  id: string;
  firstName: string;
  lastName: string;
  companyName?: string | null;
  phone?: string | null;
  email?: string | null;
  state?: string | null;
  source: LeadSource;
  status: LeadStatus;
  interestLevel: InterestLevel;
  lastContactDate?: string | null;
  nextFollowUpDate?: string | null;
  totalCallAttempts: number;
  totalConnections: number;
  assignedMarketerUserId: string;
  assignedAt?: string | null;
  assignedByUserId?: string | null;
  convertedToBrokerId?: string | null;
  isArchived: boolean;
  createdAt: string;
}

export interface CallLogDto {
  id: string;
  brokerLeadId?: string | null;
  brokerId?: string | null;
  callDate: string;
  durationSeconds?: number | null;
  disposition: CallDisposition;
  notes?: string | null;
  calledByUserId: string;
}

export interface BrokerTaskDto {
  id: string;
  title: string;
  description?: string | null;
  dueDate: string;
  reminderDate?: string | null;
  priority: TaskPriority;
  status: BrokerTaskStatus;
  taskType: BrokerTaskType;
  brokerLeadId?: string | null;
  brokerId?: string | null;
  assignedToUserId: string;
  completedDate?: string | null;
  completedNotes?: string | null;
  createdAt: string;
}

export interface BrokerLeadDetailDto {
  id: string;
  firstName: string;
  lastName: string;
  companyName?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  source: LeadSource;
  sourceDetail?: string | null;
  status: LeadStatus;
  interestLevel: InterestLevel;
  notes?: string | null;
  lastContactDate?: string | null;
  nextFollowUpDate?: string | null;
  totalCallAttempts: number;
  totalConnections: number;
  assignedMarketerUserId: string;
  assignedAt?: string | null;
  assignedByUserId?: string | null;
  convertedToBrokerId?: string | null;
  convertedDate?: string | null;
  convertedByUserId?: string | null;
  importBatchId?: string | null;
  disqualificationReason?: DisqualificationReason | null;
  disqualificationNotes?: string | null;
  createdAt: string;
  modifiedAt?: string | null;
  isArchived: boolean;
  archivedAtUtc?: string | null;
  callLogs: CallLogDto[];
  tasks: BrokerTaskDto[];
  // Lead Generation — inbound attribution. When set, this lead is credited to a
  // Lead Generator; sourcePreleadId is the linked/created prelead, and
  // sourceGeneratorName is resolved server-side for the Ownership card.
  sourceGeneratorId?: string | null;
  sourceGeneratorName?: string | null;
  sourcePreleadId?: string | null;
  /** Computed server-side: companyName when set, else "firstName lastName". */
  displayName: string;
}

export interface UpsertBrokerLeadPayload {
  firstName: string;
  lastName: string;
  companyName?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  source: LeadSource;
  sourceDetail?: string | null;
  interestLevel: InterestLevel;
  notes?: string | null;
  nextFollowUpDate?: string | null;
  assignedMarketerUserId?: string | null;
  // Lead Generation — inbound attribution set on the New Lead form. Setting it
  // links the generator's matching prelead (or creates one) and marks it
  // Escalated. Existing leads change/clear via the dedicated setLeadGenerator call.
  sourceGeneratorId?: string | null;
  // Paste to create a Lead — provenance of an AI-extracted lead. Both are set
  // only by the Paste-to-create flow: the verbatim pasted text and the model id
  // reported by the extraction endpoint. Null/absent for hand-typed leads.
  rawPastedText?: string | null;
  aiModel?: string | null;
}

/** Paste to create a Lead — the AI-extracted field set returned by
 *  POST /leads/extract-from-text. Every field is a best-effort suggestion the
 *  staff reviewer corrects before saving; `source` defaults server-side and
 *  `modelUsed` is echoed back onto the create payload's `aiModel`. */
export interface LeadExtractionResult {
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  source: LeadSource;
  notesSummary?: string | null;
  modelUsed: string;
}

/** A minimal active-generator option for the Lead Ownership picker. */
export interface LeadGeneratorOption {
  id: string;
  displayName: string;
  email?: string | null;
}

export interface LeadConversionPreviewDto {
  callLogCount: number;
  taskCount: number;
  completedTaskCount: number;
  noteCount: number;
  activityEventCount: number;
  state?: string | null;
  territoryName?: string | null;
  projectedLiaisonUserId?: string | null;
  projectedLiaisonIsPrimary?: boolean;
  projectedAssignmentWarning?: string | null;
}

export interface LeadActivityDto {
  id: string;
  leadId: string;
  activityType:
    | "LeadCreated" | "StatusChanged" | "CallLogged" | "NoteAdded"
    | "TaskCreated" | "TaskCompleted" | "DocumentUploaded" | "ConvertedToBroker";
  description: string;
  metadataJson?: string | null;
  performedByUserId: string;
  timestamp: string;
}

export interface LeadConversionResultDto {
  brokerId: string;
  brokerName: string;
  assignedLiaisonUserId?: string | null;
  territory?: string | null;
  message: string;
}

// Series 6 — liaison-initiated invite-to-apply. Token is intentionally
// not exposed; the prospect receives it by email.
export interface LeadInviteToApplyResultDto {
  brokerId: string;
  applicationId: string;
  contactEmail: string;
  magicLinkExpiresAt?: string | null;
  message: string;
  // Lead Conversion Router — set only on the Brokerage target (the lead became a
  // FIRM); the caller deep-links to /brokerages/{id} instead of /brokers/{id}.
  brokerageId?: string | null;
}

// Lead Conversion Router — what the lead should become on Send Application Link.
// Wire values match the C# enum member names (JsonStringEnumConverter).
export type LeadConversionTarget =
  | "IndependentBroker"
  | "JuniorBroker"
  | "Brokerage";

export interface LeadDuplicateMatchDto {
  leadId?: string | null;
  brokerId?: string | null;
  name: string;
  matchType: "phone" | "email" | "company" | string;
  status?: string | null;
}

export interface BrokerLeadListQuery extends PagedQuery {
  status?: LeadStatus;
  source?: LeadSource;
  state?: string;
  interestLevel?: InterestLevel;
  assignedTo?: string;
  hasFollowUp?: boolean;
  importBatchId?: string;
  /** Leads Archive v1 — when true, return ONLY archived leads (the "Archived"
   *  filter view). Omitted/false excludes archived (the default). */
  archived?: boolean;
}

/** Aggregate counts for the leads page stat cards. <code>total</code> is
 *  the count under every active filter except <code>status</code>;
 *  <code>byStatus</code> partitions that same universe by status. */
export interface BrokerLeadStatsDto {
  total: number;
  byStatus: Record<LeadStatus, number>;
}

// Activity Timeline UX §1 — query types for the paged history endpoints.
export interface CallLogListQuery extends PagedQuery {
  disposition?: CallDisposition;
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
  search?: string;
}

export interface BrokerTaskListQuery extends PagedQuery {
  status?: BrokerTaskStatus;
  assignedTo?: string;
  todayOnly?: boolean;
  overdueOnly?: boolean;
  brokerLeadId?: string;
  brokerId?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  search?: string;
}

export interface LeadImportBatchDto {
  id: string;
  fileName: string;
  totalRows: number;
  importedCount: number;
  duplicateCount: number;
  errorCount: number;
  status: ImportStatus;
  errorLog?: string | null;
  importedByUserId: string;
  createdAt: string;
}

export const leadsApi = {
  list: (query?: BrokerLeadListQuery) =>
    api.get<PagedResult<BrokerLeadSummaryDto>>("/leads", query),
  stats: (query?: BrokerLeadListQuery) =>
    api.get<BrokerLeadStatsDto>("/leads/stats", query),
  get: (id: string) => api.get<BrokerLeadDetailDto>(`/leads/${id}`),
  /** Lead Generation — the source prelead this lead was escalated from (read-only
   *  for lead handlers). Rides Leads.Read so C1-C5 can open it without LeadGen.View.
   *  404 when there is no source prelead or it was soft-deleted. */
  sourcePrelead: (id: string) =>
    api.get<PreleadDetailDto>(`/leads/${id}/source-prelead`),
  create: (body: UpsertBrokerLeadPayload) =>
    api.post<BrokerLeadDetailDto>("/leads", body),
  update: (id: string, body: UpsertBrokerLeadPayload) =>
    api.put<BrokerLeadDetailDto>(`/leads/${id}`, body),
  delete: (id: string) => api.delete<void>(`/leads/${id}`),
  changeStatus: (
    id: string,
    targetStatus: LeadStatus,
    disqualificationReason?: DisqualificationReason,
    disqualificationNotes?: string,
  ) =>
    api.patch<BrokerLeadDetailDto>(`/leads/${id}/status`, {
      targetStatus,
      disqualificationReason,
      disqualificationNotes,
    }),
  // Leads Archive v1 — reversible soft-archive of a disqualified lead (single
  // archive/unarchive ride Leads.Update). bulkArchive is admin-only and the
  // server skips any non-disqualified ids, returning the count archived.
  archive: (id: string) => api.post<BrokerLeadDetailDto>(`/leads/${id}/archive`),
  unarchive: (id: string) => api.post<BrokerLeadDetailDto>(`/leads/${id}/unarchive`),
  bulkArchive: (leadIds: string[]) => api.post<number>("/leads/bulk-archive", { leadIds }),
  convert: (id: string) => api.post<LeadConversionResultDto>(`/leads/${id}/convert`),
  inviteToApply: (
    id: string,
    body: {
      collectionBundleIds: string[];
      sendPortalAccessOnly: boolean;
      // Lead Conversion Router — omitted = IndependentBroker (today's behaviour).
      target?: LeadConversionTarget;
      brokerageId?: string | null;
    },
  ) =>
    api.post<LeadInviteToApplyResultDto>(`/leads/${id}/invite-to-apply`, body),
  conversionPreview: (id: string) =>
    api.get<LeadConversionPreviewDto>(`/leads/${id}/conversion-preview`),
  activity: (id: string) => api.get<LeadActivityDto[]>(`/leads/${id}/activity`),
  // Ownership/Assignment §2 — reassign the marketer who owns this lead.
  assign: (id: string, userId: string) =>
    api.put<BrokerLeadDetailDto>(`/leads/${id}/assign`, { userId }),
  // Lead Generation — active-generator options for the Ownership picker (name search).
  leadGeneratorOptions: (q?: string) =>
    api.get<LeadGeneratorOption[]>("/leads/lead-generator-options", { q }),
  // Lead Generation — set (or clear, with null) the lead's attributed generator.
  setLeadGenerator: (id: string, sourceGeneratorId: string | null) =>
    api.put<BrokerLeadDetailDto>(`/leads/${id}/lead-generator`, { sourceGeneratorId }),
  duplicates: (phone?: string, email?: string, companyName?: string) =>
    api.get<LeadDuplicateMatchDto[]>("/leads/duplicates", { phone, email, companyName }),
  // Paste to create a Lead — AI extraction of a pasted blob into a draft lead.
  // Throws ApiError with status 422 when the text can't be parsed automatically.
  extractFromText: (text: string) =>
    api.post<LeadExtractionResult>("/leads/extract-from-text", { text }),
  // Layout Compliance v1.0 PR 6 — `calls` / `logCall` / `tasks` retired
  // alongside the ActivityHistoryPanel slide-over. Communications and
  // Tasks for a Lead live on the canonical cross-cutting tabs, which
  // read CommunicationLogEntries and WorkTasks. Lead-scope CallLog and
  // BrokerTask rows were backfilled into those tables by migration
  // 20270301000000_LayoutLead_BackfillCommLogAndWorkTasks (PR 5).
  import: (file: File, defaultSource: LeadSource = "PurchasedList") => {
    const form = new FormData();
    form.append("file", file);
    form.append("defaultSource", defaultSource);
    return api.upload<LeadImportBatchDto>("/leads/import", form);
  },
  importStatus: (batchId: string) =>
    api.get<LeadImportBatchDto>(`/leads/import/${batchId}`),
};
